<template>
  <section class="section is-medium faq-section">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.faq-title') }}
        </h2>
      </div>
      
      <div class="columns is-centered">
        <div class="column is-10 is-8-desktop">
          <div class="faq-list">
            <div
              v-for="(faq, index) in faqs"
              :key="index" 
              class="faq-item animate fade-in-up" 
              :class="[
                `animate--delay-${Math.min(index, 3)}`,
                { 'is-active': activeIndex === index }
              ]"
              @click="toggleFaq(index)"
            >
              <div class="faq-question">
                <h3>{{ $t(faq.question) }}</h3>
                <div class="faq-icon">
                  <i
                    class="fas"
                    :class="activeIndex === index ? 'fa-minus' : 'fa-plus'"
                  />
                </div>
              </div>
              <div
                v-show="activeIndex === index"
                class="faq-answer"
              >
                <p>{{ $t(faq.answer) }}</p>
              </div>
            </div>
          </div>

          <div class="glossary-callout animate fade-in-up">
            <div class="glossary-callout-icon">
              <i class="fas fa-book" />
            </div>
            <div class="glossary-callout-content">
              <h3>{{ $t('glossary.callout-title') }}</h3>
              <p>{{ $t('glossary.callout-subtitle') }}</p>
            </div>
            <router-link
              to="/distribution-glossary"
              class="glossary-callout-link"
            >
              {{ $t('glossary.callout-cta') }}
              <i class="fas fa-arrow-right ml-2" />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '@vueuse/head';

const { t } = useI18n();

const activeIndex = ref(0);

const faqs = [
  {
    question: 'landing.faq-item1-question',
    answer: 'landing.faq-item1-answer',
  },
  {
    question: 'landing.faq-item2-question',
    answer: 'landing.faq-item2-answer',
  },
  {
    question: 'landing.faq-item3-question',
    answer: 'landing.faq-item3-answer',
  },
  {
    question: 'landing.faq-item4-question',
    answer: 'landing.faq-item4-answer',
  },
  {
    question: 'landing.faq-item5-question',
    answer: 'landing.faq-item5-answer',
  },
  {
    question: 'landing.faq-item6-question',
    answer: 'landing.faq-item6-answer',
  },
];

const faqJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: t(faq.question),
    acceptedAnswer: {
      '@type': 'Answer',
      text: t(faq.answer),
    },
  })),
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      key: 'faq-page-jsonld',
      children: computed(() => JSON.stringify(faqJsonLd.value)),
    },
  ],
});

const toggleFaq = (index) => {
  if (activeIndex.value === index) {
    activeIndex.value = -1;
  } else {
    activeIndex.value = index;
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/animations";

.faq-section {
  background-color: var(--color-background, #fff);
}

.title.has-line {
  position: relative;
  padding-bottom: 1.5rem;
  color: var(--color-text, #000);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    border-radius: 2px;
  }
}

.faq-list {
  margin-top: 2rem;
}

.faq-item {
  background-color: var(--color-background, white);
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
  
  &.is-active {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    border-left: 3px solid $indiefy-blue;
    
    .faq-question {
      h3 {
        color: $indiefy-blue;
      }
    }
  }
}

.faq-question {
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
    color: var(--color-text, #333);
    transition: color 0.3s ease;
  }
  
  .faq-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $indiefy-blue;
    transition: transform 0.3s ease;
    margin-left: 1rem;
    flex-shrink: 0;
  }
}

.faq-answer {
  padding: 0 1.5rem 1.5rem;
  
  p {
    font-size: 1rem;
    line-height: 1.6;
    margin: 0;
    color: var(--color-text-light, #666);
  }
}

.glossary-callout {
  margin-top: 2.5rem;
  padding: 1.75rem 2rem;
  background: linear-gradient(135deg, rgba($indiefy-blue, 0.08), rgba($indiefy-pink, 0.08));
  border: 1px solid rgba($indiefy-blue, 0.15);
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;

  .body--dark & {
    background: linear-gradient(135deg, rgba($indiefy-blue, 0.15), rgba($indiefy-pink, 0.12));
    border-color: rgba($indiefy-blue, 0.3);
  }

  .glossary-callout-icon {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: linear-gradient(135deg, $indiefy-blue, $indiefy-lavender);
    color: #fff;
    font-size: 1.5rem;
    flex-shrink: 0;
    box-shadow: 0 6px 16px rgba($indiefy-blue, 0.3);
  }

  .glossary-callout-content {
    flex: 1;
    min-width: 200px;

    h3 {
      font-size: 1.15rem;
      font-weight: 700;
      margin: 0 0 0.25rem;
      color: var(--color-text, #1e293b);
    }

    p {
      margin: 0;
      color: var(--color-text-light, #64748b);
      font-size: 0.95rem;
    }
  }

  .glossary-callout-link {
    display: inline-flex;
    align-items: center;
    background: $indiefy-blue;
    color: #fff;
    padding: 0.7rem 1.4rem;
    border-radius: 50px;
    font-weight: 600;
    text-decoration: none;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    box-shadow: 0 4px 12px rgba($indiefy-blue, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 18px rgba($indiefy-blue, 0.4);
      color: #fff;
    }
  }

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;

    .glossary-callout-link {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>